import clsx from 'clsx'
import useToggle from 'hooks/useToggle'
import { rem } from 'polished'
import { forwardRef, useCallback, useImperativeHandle, useRef } from 'react'
import * as React from 'react'
import styled from 'styled-components'
import { mediaQueryUp } from 'components/utils/breakpoint'
import noop from 'lib/function/noop'
import DropdownChevron from 'LuxKit/Dropdown/DropdownChevron'
import TextLink from 'LuxKit/TextLink'
import { useElementHeight } from 'hooks/useElementSize'
import useHasMounted from 'hooks/useHasMounted'

const LargeContentWrapper = styled.div`
  position: relative;
  overflow: hidden;

  &.enable-transition {
    transition: max-height 0.2s;
  }

  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    inset-inline: 0;
    height: ${rem(32)};
    background: linear-gradient(to bottom, rgba(255,255,255,0), rgba(255,255,255,1));
    opacity: 0;
    transition: opacity 0.2s;
    pointer-events: none;
  }

  &.show-mask::after {
    opacity: 1;
  }
`

const SwitchButton = styled(TextLink)`
  margin-top: ${rem(8)};

  ${mediaQueryUp.tablet} {
    justify-self: start;
  }
`

interface Props {
  children: React.ReactNode
  maxHeight: number
  collapsedHeight?: number
  showMoreText?: string
  showLessText?: string
  showChevron?: boolean
  hideText?: boolean
  open?: boolean
  defaultOpen?: boolean
  onToggle?: () => void
  minimumContentHeight?: number
  /**
   * Fixes expanded content being clipped when layout height is under-reported.
   * CSS multi-column layouts (for example column-count with break-inside: avoid)
   * can report a smaller height than the content actually paints, so capping
   * expanded max-height to the measured value may still hide the bottom of the
   * section. When true, expanded content uses max-height: none instead.
   * Defaults to false so existing consumers keep the measured-height behaviour.
   */
  unconstrainedWhenExpanded?: boolean
  /**
   * Font weight for the show more / show less toggle link.
   * @default 'bold'
   */
  toggleWeight?: 'regular' | 'bold'
}

export interface LargeContentToggleHandle {
  expand: () => void
}

function LargeContentToggle(props: Props, ref: React.Ref<LargeContentToggleHandle>) {
  const {
    defaultOpen,
    children,
    maxHeight,
    collapsedHeight = maxHeight,
    showMoreText,
    showLessText,
    hideText = false,
    showChevron = true,
    open,
    onToggle = noop,
    minimumContentHeight = 0,
    unconstrainedWhenExpanded = false,
    toggleWeight = 'bold',
  } = props
  const [showAll, toggleAll, expandAll] = useToggle(defaultOpen)
  useImperativeHandle(ref, () => ({ expand: expandAll }), [expandAll])
  const contentElement = useRef<HTMLDivElement>(null)
  const isControlled = open !== undefined

  const onToggleClick = useCallback(() => {
    onToggle()
    if (!isControlled) {
      toggleAll()
    }
  }, [toggleAll, isControlled, onToggle])

  const contentHeight = useElementHeight(contentElement) ?? maxHeight

  const adjustedContentHeight = Math.max(minimumContentHeight, contentHeight)
  const isTaller = adjustedContentHeight > maxHeight
  const expanded = !isTaller || (isControlled ? open : showAll)

  const hasMounted = useHasMounted()
  const expandedMaxHeight = expanded
    ? (unconstrainedWhenExpanded ? 'none' : adjustedContentHeight)
    : collapsedHeight

  return (<>
    <LargeContentWrapper
      className={clsx({ 'show-mask': !expanded, 'enable-transition': hasMounted })}
      style={{ maxHeight: expandedMaxHeight }}
    >
      <div ref={contentElement}>
        {children}
      </div>
    </LargeContentWrapper>
    {!hideText && isTaller &&
      <SwitchButton
        size="medium"
        weight={toggleWeight}
        aria-expanded={expanded}
        endIcon={showChevron ? <DropdownChevron open={expanded} /> : undefined}
        onClick={onToggleClick}
      >
        {expanded ? showLessText : showMoreText}
      </SwitchButton>}
  </>)
}

export default forwardRef(LargeContentToggle)
